import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DefaultTheme, withTheme } from 'styled-components';
import DOMPurify from 'dompurify';
import {
    BlockTitleContainer,
    MediaContainer,
    ContentContainer,
    MediaBlockItem,
    MediaBlockImage,
    MediaBlockDescription,
    MediaBlockTitle,
    MediaBlockButton,
    MediaBlockIcon,
    MediaBlockMoreBtn,
} from './styled';
import GradientTitle from 'components/gradient-title/GradientTitle';
import { HttpResponse, Media, Tag } from 'types';
import { MediaType } from 'enums/media-type';
import { Icon, PaginationOutput } from 'kl-b2c-ui-kit';
import Tags from 'components/tags/Tags';
import { useNavigate } from 'react-router-dom';
import { getApiService, getMediaApi, getMediaTagsApi, getStorageService } from 'services';

const MediaBlock: FC<{ theme: DefaultTheme }> = ({ theme }) => {
    const { t } = useTranslation('pages/home');
    const [activeMediaBlock, setActiveMediaBlock] = useState<number>(0);
    const [media, setMedia] = useState<Media[] | null>(null);
    const [mediaTags, setMediaTags] = useState<Tag[] | null>();
    const [chosenTags, setChosenTags] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const navigate = useNavigate();

    const storageService = getStorageService();
    const apiService = getApiService(storageService);
    const mediaTagsApiService = getMediaTagsApi(apiService);
    const mediaApiService = getMediaApi(apiService);

    useEffect(() => {
        const mediaTagsPromise = mediaTagsApiService.get({ page: 0, size: 300, withMedias: true, withHomePage: true });
        const mediaPromise = mediaApiService.get({
            skip: 0,
            take: 3,
            isVisibleHomePage: true,
            isVisible: true,
        });

        Promise.all([mediaTagsPromise, mediaPromise]).then(([a, b]) => {
            setMediaTags(a.data.items);
            setMedia(b.data.items);
        });
    }, []);

    useEffect(() => {
        if (chosenTags) {
            setLoading(true);
            mediaApiService
                .get({
                    skip: 0,
                    take: 3,
                    isVisibleHomePage: true,
                    isVisible: true,
                    tagIds: chosenTags,
                })
                .then((media: HttpResponse<PaginationOutput<Media>>) => {
                    setMedia(media.data.items);
                    setLoading(false);
                });
        }
    }, [chosenTags]);

    if (!media?.length || !mediaTags?.length) return null;

    return (
        <MediaContainer>
            <BlockTitleContainer>
                <GradientTitle
                    variant="h1"
                    label={t('mediaBlock-title')}
                    startColor="#BBFF33"
                    finishColor="#53F592"
                    startLabelColor={theme.palette.black}
                    finishLabelColor={theme.palette.white}
                />
            </BlockTitleContainer>

            {media.length === 1 && chosenTags.length === 0 && !loading ? null : (
                <Tags
                    onChange={(chosen) => {
                        setChosenTags(chosen);
                        setActiveMediaBlock(0);
                    }}
                    tags={mediaTags.map(({ id, name }) => ({ key: id, label: name }))}
                />
            )}

            <ContentContainer>
                {media.map((item: Media, index: number) => (
                    <MediaBlockItem
                        key={item.id}
                        isActive={index === activeMediaBlock}
                        length={media.length}
                        onMouseEnter={() => setActiveMediaBlock(index)}
                        href={item.link}
                        target={'_blank'}
                        rel={'noopener noreferrer'}
                    >
                        <MediaBlockImage url={item.previewImage.fileLink}>
                            <MediaBlockButton to={item.link} target={'_blank'} isVisible={index === activeMediaBlock}>
                                {item.type === MediaType.Article ? (
                                    <Icon name={'Article'} size={22} color="black" />
                                ) : (
                                    <Icon name={'PlayFilled'} size={15} color="black" />
                                )}
                                <span>{item.type === MediaType.Article ? t('article') : t('video')}</span>
                            </MediaBlockButton>

                            <MediaBlockIcon isVisible={index !== activeMediaBlock}>
                                {item.type === MediaType.Article ? (
                                    <Icon name={'Article'} size={24} color="white" />
                                ) : (
                                    <Icon name={'PlayStroke'} size={24} color="white" />
                                )}
                            </MediaBlockIcon>
                        </MediaBlockImage>
                        <MediaBlockTitle>{item.title}</MediaBlockTitle>
                        <MediaBlockDescription
                            dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(item.description),
                            }}
                        />
                    </MediaBlockItem>
                ))}
            </ContentContainer>

            {media && media.length >= 1 && (
                <MediaBlockMoreBtn onClick={() => navigate('/media')}>{t('show-more-videos')}</MediaBlockMoreBtn>
            )}
        </MediaContainer>
    );
};

export default withTheme(MediaBlock);
